import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
// Assuming endpoint and apiHandler are correctly imported
import { endpoint } from "../../api/endpoint"; 
import { apiHandler } from "../../api";

const DEBOUNCE_DELAY = 500; // ms

const normalizeStringList = (list) => {
  const set = new Set();
  (list || []).forEach((item) => {
    if (!item) return;
    if (typeof item === "string" && item.trim()) {
      set.add(item.trim());
    } else if (typeof item === "object") {
      const val = item.domain || item.name || item.value || item.id;
      if (val && typeof val === "string" && val.trim()) {
        set.add(val.trim());
      }
    }
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

const normalizeBapList = (list) => {
  const map = new Map();
  (list || []).forEach((item) => {
    if (!item) return;
    if (typeof item === "object") {
      const val = item.bap_id || item.subscriber_id || item.name || item.id;
      if (val && typeof val === "string" && val.trim()) {
        const trimmed = val.trim();
        if (!map.has(trimmed)) {
          map.set(trimmed, item);
        }
      }
    } else if (typeof item === "string" && item.trim()) {
      const trimmed = item.trim();
      if (!map.has(trimmed)) {
        map.set(trimmed, trimmed);
      }
    }
  });
  return Array.from(map.values()).sort((a, b) => {
    const labelA = (typeof a === "object" ? a.name || a.bap_id || "" : a).toString();
    const labelB = (typeof b === "object" ? b.name || b.bap_id || "" : b).toString();
    return labelA.localeCompare(labelB);
  });
};

export const useCatalogueRejections = () => {
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromUrl = parseInt(searchParams.get("page"), 10);
  const initialPage = !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
  const limitFromUrl = parseInt(searchParams.get("limit") || searchParams.get("orders_per_page"), 10);
  const initialLimit = !isNaN(limitFromUrl) && limitFromUrl > 0 ? limitFromUrl : 10;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [analytics, setAnalytics] = useState({});

  // Corrected and consolidated initial filters.
  const [filters, setFilters] = useState({
    city: "",
    domain: "",
    bap_id: "",
    catalog_status_code: "",
    catalog_rejection_type: "",
    catalog_resolved_status: "", // Added as per API needs, though currently not filtered in component
    created_from: "",
    created_to: "",
    search_by: "",
    search_value: "",
    sort_by: "createdAt",    // <-- Default API field
    sort_order: "DESC",      // <-- Default API direction (using uppercase to match server)
  });

  const [filterOptions, setFilterOptions] = useState({
    city: [],
    domain: [],
    catalog_status_code: [],
    catalog_rejection_type: [],
    bap_id: [],
  });

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);

  const filterOptionsFetched = useRef(false);
  const debounceTimer = useRef(null);

  // Fetch filter options: Domains from GET_CATALOGUE_REJECTION_DOMAIN and BAP IDs from GET_CATALOGUE_REJECTION_BAP_ID
  const getFilterOptions = useCallback(async () => {
    if (!authData || filterOptionsFetched.current) return;
    filterOptionsFetched.current = true;

    try {
      // 1. Fetch Domains from GET_CATALOGUE_REJECTION_DOMAIN API
      const domainPromise = apiHandler({
        url: endpoint.GET_CATALOGUE_REJECTION_DOMAIN,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      }).catch(() => null);

      // 2. Fetch BAP IDs from GET_CATALOGUE_REJECTION_BAP_ID API
      const bapPromise = apiHandler({
        url: endpoint.GET_CATALOGUE_REJECTION_BAP_ID,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      }).catch(() => null);

      // 3. Fetch status codes & rejection types from GET_CATALOGUE_REJECTION
      const catRejectionPromise = apiHandler({
        url: `${endpoint.GET_CATALOGUE_REJECTION}?page=1&limit=50&orders_per_page=50`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      }).catch(() => null);

      const [domainRes, bapRes, catRes] = await Promise.all([
        domainPromise,
        bapPromise,
        catRejectionPromise,
      ]);

      const rawDomains =
        domainRes?.data?.data ||
        domainRes?.data?.domains ||
        domainRes?.data?.domainList ||
        (Array.isArray(domainRes?.data) ? domainRes?.data : []);

      const rawBapIds =
        bapRes?.data?.data ||
        bapRes?.data?.bap_ids ||
        bapRes?.data?.bapData ||
        (Array.isArray(bapRes?.data) ? bapRes?.data : []);

      const discoveredStatuses = new Set();
      const discoveredRejectionTypes = new Set();
      const discoveredCities = new Set();

      const directFilters =
        catRes?.data?.filters ||
        catRes?.data?.data?.filters ||
        catRes?.data?.data?.filter;

      if (directFilters) {
        (directFilters.city || directFilters.cities || []).forEach((c) =>
          discoveredCities.add(c)
        );
        (
          directFilters.catalog_status_code ||
          directFilters.status_codes ||
          directFilters.status ||
          []
        ).forEach((s) => discoveredStatuses.add(s));
        (
          directFilters.catalog_rejection_type ||
          directFilters.rejection_types ||
          directFilters.types ||
          []
        ).forEach((t) => discoveredRejectionTypes.add(t));
      }

      const firstPageData = catRes?.data?.data?.data || [];
      firstPageData.forEach((item) => {
        if (item.catalog_status_code) discoveredStatuses.add(item.catalog_status_code);
        if (item.catalog_rejection_type) discoveredRejectionTypes.add(item.catalog_rejection_type);
        if (item.city) discoveredCities.add(item.city);
      });

      setFilterOptions((prev) => ({
        ...prev,
        domain: normalizeStringList(rawDomains),
        bap_id: normalizeBapList(rawBapIds),
        city: normalizeStringList([
          ...(prev?.city || []),
          ...Array.from(discoveredCities),
        ]),
        catalog_status_code: normalizeStringList([
          ...(prev?.catalog_status_code || []),
          ...Array.from(discoveredStatuses),
        ]),
        catalog_rejection_type: normalizeStringList([
          ...(prev?.catalog_rejection_type || []),
          ...Array.from(discoveredRejectionTypes),
        ]),
      }));
    } catch (err) {
      // ignore filter options fetch error
    }
  }, [authData, dispatch, storeId]);

  useEffect(() => {
    if (authData) {
      getFilterOptions();
    }
  }, [authData, getFilterOptions]);

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      // Ensure the URL construction starts correctly
      let url = `${endpoint.GET_CATALOGUE_REJECTION}`;

      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        const cleanValue = value ? value.toString().trim() : "";
        if (cleanValue === "") return;

        if (key === "search_value" || key === "search_by") return;

        // 1. Rename sort_order to sort_direction and force uppercase
        if (key === "sort_order") {
          params.append("sort_direction", cleanValue.toUpperCase());
          return;
        }

        // 2. Map sort_by 
        if (key === "sort_by") {
          params.append("sort_by", cleanValue);
          return;
        }

        // 3. Map created_from/created_to to start_date/end_date (like Orders)
        if (key === "created_from") {
          params.append("start_date", cleanValue);
          return;
        }

        if (key === "created_to") {
          params.append("end_date", cleanValue);
          return;
        }

        // Add all other non-search, non-pagination, non-sort filters
        params.append(key, cleanValue);
      });

      // Handle search safely
      if (filters.search_by && filters.search_value) {
        const searchVal = filters.search_value.toString().trim();
        params.append(filters.search_by, searchVal);
      }

      // 3. Rename 'limit' to 'orders_per_page'
      params.append("page", currentPage);
      params.append("orders_per_page", itemsPerPage); // <-- CORRECTED PARAMETER NAME

      let finalUrl = url;
      if (params.toString()) {
        finalUrl += `?${params.toString()}`;
      }
      

      const result = await apiHandler({
        url: finalUrl,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      

      if (result?.data?.data?.data) {
        const respData = result.data.data.data;
        setData(respData);
        setTotalCount(result.data.data.totalCount || 0);

        // Extract filter values from both backend filters and current records
        const apiFilters =
          result?.data?.filters ||
          result?.data?.data?.filters ||
          result?.data?.data?.filter ||
          {};

        const backendCities = apiFilters.city || apiFilters.cities || [];
        const backendStatuses =
          apiFilters.catalog_status_code ||
          apiFilters.status_codes ||
          apiFilters.status ||
          [];
        const backendRejectionTypes =
          apiFilters.catalog_rejection_type ||
          apiFilters.rejection_types ||
          apiFilters.types ||
          [];

        const recordStatuses = [];
        const recordRejectionTypes = [];
        const recordCities = [];

        respData.forEach((item) => {
          if (item.catalog_status_code) recordStatuses.push(item.catalog_status_code);
          if (item.catalog_rejection_type) recordRejectionTypes.push(item.catalog_rejection_type);
          if (item.city) recordCities.push(item.city);
        });

        setFilterOptions((prev) => ({
          ...prev,
          city: normalizeStringList([
            ...(prev?.city || []),
            ...backendCities,
            ...recordCities,
          ]),
          catalog_status_code: normalizeStringList([
            ...(prev?.catalog_status_code || []),
            ...backendStatuses,
            ...recordStatuses,
          ]),
          catalog_rejection_type: normalizeStringList([
            ...(prev?.catalog_rejection_type || []),
            ...backendRejectionTypes,
            ...recordRejectionTypes,
          ]),
        }));
      } else {
         setData([]);
         setTotalCount(0);
      }

      if (result?.data?.analytics) {
        setAnalytics(result.data.analytics);
      } else {
         setAnalytics({});
      }

    } catch (err) {
      //console.error("Error fetching catalog rejection data:", err);
      setData([]);
      setTotalCount(0);
      setAnalytics({});
    } finally {
      setLoading(false);
    }
  }, [
    // Consolidated dependencies for stability
    filters, 
    currentPage, 
    itemsPerPage, 
    authData, 
    dispatch, 
    storeId, 
  ]); // Note: filter properties are included via 'filters' dependency

  // The useEffect hook remains the same, ensuring debouncing works
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    setLoading(true); 

    debounceTimer.current = setTimeout(() => {
      getData();
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(debounceTimer.current);
  }, [
    filters.search_value,
    filters.search_by,
    currentPage,
    itemsPerPage,
    filters.city,
    filters.domain,
    filters.bap_id,
    filters.catalog_status_code,
    filters.catalog_rejection_type,
    filters.created_from,
    filters.created_to,
    filters.sort_order,
    filters.sort_by, // Added sort_by as a dependency
    getData
  ]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = useCallback(
    (newPage) => {
      const pageNum = typeof newPage === "function" ? newPage(currentPage) : newPage;
      const validPage = Math.max(1, pageNum);
      setCurrentPage(validPage);
      const next = new URLSearchParams(window.location.search);
      if (validPage <= 1) {
        next.delete("page");
      } else {
        next.set("page", validPage.toString());
      }
      setSearchParams(next, { replace: true });
    },
    [currentPage, setSearchParams]
  );

  // Sync state if URL search params change
  useEffect(() => {
    const pageInUrl = parseInt(searchParams.get("page"), 10);
    const validPage = !isNaN(pageInUrl) && pageInUrl > 0 ? pageInUrl : 1;
    setCurrentPage((prev) => (prev !== validPage ? validPage : prev));

    const limitInUrl = parseInt(searchParams.get("limit") || searchParams.get("orders_per_page"), 10);
    if (!isNaN(limitInUrl) && limitInUrl > 0) {
      setItemsPerPage((prev) => (prev !== limitInUrl ? limitInUrl : prev));
    }
  }, [searchParams]);

  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage) => {
      const limitNum = Number(newItemsPerPage);
      setItemsPerPage(limitNum);
      setCurrentPage(1);
      const next = new URLSearchParams(window.location.search);
      next.delete("page");
      if (limitNum === 10) {
        next.delete("limit");
      } else {
        next.set("limit", limitNum.toString());
      }
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  return {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage: handlePageChange,
    itemsPerPage,
    setItemsPerPage: handleItemsPerPageChange,
    totalPages,
    analytics,
  };
};